import * as React from 'react'

import {
  SimpleAccordion,
  SimpleAccordionDetails,
} from '@sales-center/components'
import { currencyString } from '@sales-center/common'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import { useData } from './hooks'
import { ServiceCardProps } from './typings'
import AccordionSummary, {
  accordionSummaryClasses,
} from '@mui/material/AccordionSummary'
import Stack from '@mui/material/Stack'
import { useLocalizationEnabled } from '@user/store/tenantFeatures/hooks/useLocalizationEnabled'
import { Theme, useTheme } from '@mui/material/styles'

export function ServiceCard(props: ServiceCardProps) {
  const { service, index, frequency, entityType } = props

  const {
    name,
    taxRate,
    quantity,
    chargeTax,
    totalFirst,
    description,
    initialPrice,
    recurringPrice,
    canadianTaxRate,
    gstHstRate,
    pstQstRate,
    gstHstLabel,
    pstQstLabel,
  } = service

  const isCanadianTax = useLocalizationEnabled() && canadianTaxRate

  const {
    totalTax,
    serviceNumber,
    initialDiscount,
    recurringDiscount,
    gstTaxTotal,
    pstTaxTotal,
  } = useData({
    index,
    service,
    entityType,
  })
  const theme = useTheme()
  return (
    <Card
      square
      elevation={1}
      sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
    >
      <SimpleAccordion square>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon data-test-id="service-card-expandIcon" />}
          sx={{
            padding: theme.spacing(0),
            marginTop: theme.spacing(0),
            marginBottom: theme.spacing(0),
            [`& .${accordionSummaryClasses.content}`]: {
              marginTop: theme.spacing(0),
            },
            marginRight: theme.spacing(1.6),
          }}
        >
          <Box
            display="flex"
            flexGrow={1}
            sx={{
              marginTop: (theme: Theme) => theme.spacing(1.4),
              marginLeft: (theme: Theme) => theme.spacing(1.6),
              flexDirection: 'row',
            }}
          >
            <Box>
              {' '}
              <Typography variant="body2" sx={{ width: '75px' }}>
                Service {serviceNumber}
              </Typography>
            </Box>

            <Box minWidth={212} maxWidth={212}>
              <Typography variant="subtitle2">
                {' '}
                {name}{' '}
                <Typography variant="body2" component={'span'}>
                  {' '}
                  {description}{' '}
                </Typography>
              </Typography>
            </Box>

            <Box ml={2} width={88}>
              <Typography variant="body2">
                {' '}
                Total ${currencyString(totalFirst)}
              </Typography>
            </Box>
          </Box>
        </AccordionSummary>
        <SimpleAccordionDetails>
          <Stack
            spacing={0.8}
            sx={{
              paddingLeft: theme.spacing(2),
              marginBottom: theme.spacing(1.6),
            }}
          >
            <Box>
              <Typography variant="subtitle2">Quantity</Typography>
              <Typography variant="body2">{quantity}</Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2">Frequency</Typography>
              <Typography variant="body2">{frequency?.description}</Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2">Initial Price</Typography>
              <Typography variant="body2">
                {' '}
                ${currencyString(initialPrice * quantity)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2">Initial Discount</Typography>
              <Typography variant="body2">{initialDiscount}</Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2">Recurring Discount</Typography>
              <Typography variant="body2">{recurringDiscount}</Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2">Recurring Price</Typography>
              <Typography variant="body2">
                {' '}
                ${currencyString(recurringPrice)}
              </Typography>
            </Box>

            {chargeTax &&
              (isCanadianTax ? (
                <Box>
                  <Typography variant="subtitle2">Tax</Typography>
                  {gstTaxTotal !== '' && (
                    <Box display="flex" flexDirection="row" gap={1}>
                      <Typography variant="body2" minWidth={60}>
                        {gstHstLabel} ({gstHstRate * 100}%)
                      </Typography>
                      <Typography variant="body2">{gstTaxTotal}</Typography>
                    </Box>
                  )}
                  {pstTaxTotal !== '' && (
                    <Box display="flex" flexDirection="row" gap={1}>
                      <Typography variant="body2" minWidth={60}>
                        {pstQstLabel} ({pstQstRate * 100}%)
                      </Typography>
                      <Typography variant="body2">{pstTaxTotal}</Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle2">Tax</Typography>
                  <Typography variant="body2">{totalTax}</Typography>
                  <Typography variant="body2">{taxRate * 100}%</Typography>
                </Box>
              ))}
          </Stack>
        </SimpleAccordionDetails>
      </SimpleAccordion>
    </Card>
  )
}
